import React, { useState, useEffect } from 'react';
import { Crown, CheckCircle, X, AlertCircle, Calendar, CreditCard, RefreshCw, Zap } from 'lucide-react';
import { RevenueCatService, RevenueCatProduct, RevenueCatSubscription } from '../services/revenueCatService';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import BoltBadge from './BoltBadge';

interface SubscriptionManagerProps {
  onClose?: () => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ onClose }) => {
  const [products, setProducts] = useState<RevenueCatProduct[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<RevenueCatSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [productsData, subscriptionData] = await Promise.all([
          RevenueCatService.getProducts(),
          RevenueCatService.getCurrentSubscription()
        ]);
        
        setProducts(productsData);
        setCurrentSubscription(subscriptionData);
      } catch (err) {
        setError('Failed to load subscription data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const handlePurchase = async (productId: string) => {
    try {
      setLoading(true);
      const success = await RevenueCatService.purchaseProduct(productId);
      if (!success) {
        throw new Error('Purchase failed');
      }
      // The purchase process will redirect the user
    } catch (err) {
      setError('Failed to initiate purchase');
      console.error(err);
      setLoading(false);
    }
  };

  const handleRestorePurchases = async () => {
    try {
      setLoading(true);
      const success = await RevenueCatService.restorePurchases();
      if (success) {
        const subscription = await RevenueCatService.getCurrentSubscription();
        setCurrentSubscription(subscription);
        setError(null);
      } else {
        setError('No purchases found to restore');
      }
    } catch (err) {
      setError('Failed to restore purchases');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getProductsForPeriod = () => {
    return products.filter(product => product.subscriptionPeriod === selectedPeriod);
  };

  const isSubscribedTo = (productId: string) => {
    return currentSubscription?.productId === productId && 
           currentSubscription?.status === 'active';
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 mx-auto mb-4"></div>
        <p className="text-slate-600 dark:text-slate-400">Loading subscription information...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">Subscription Management</h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm">Manage your RoomState subscription</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 dark:text-red-400 font-medium">{error}</p>
            <button
              onClick={handleRestorePurchases}
              className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Restore Purchases
            </button>
          </div>
        </div>
      )}

      {/* Current Subscription */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Current Subscription</h3>
        <div className={`p-6 rounded-xl ${
          currentSubscription?.status === 'active'
            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
            : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
        }`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-5 h-5" />
                <h4 className="text-xl font-bold">
                  {currentSubscription?.status === 'active'
                    ? products.find(p => p.id === currentSubscription.productId)?.title || 'Pro Plan'
                    : 'Free Plan'}
                </h4>
                <div className={`px-2 py-0.5 text-xs rounded-full ${
                  currentSubscription?.status === 'active'
                    ? 'bg-green-400/30 text-white'
                    : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300'
                }`}>
                  {currentSubscription?.status === 'active' ? 'Active' : 'Free Tier'}
                </div>
              </div>
              
              {currentSubscription?.status === 'active' && (
                <div className="text-white/80 text-sm mt-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Renews: {formatDate(currentSubscription.expirationDate)}
                      {currentSubscription.willRenew ? '' : ' (Cancels after this period)'}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              {currentSubscription?.status === 'active' ? (
                <a
                  href="https://app.revenuecat.com/manage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white text-sm font-medium"
                >
                  <CreditCard className="w-4 h-4" />
                  Manage Billing
                </a>
              ) : (
                <button
                  onClick={() => setSelectedPeriod('monthly')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Zap className="w-4 h-4" />
                  Upgrade Now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Options */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Subscription Plans</h3>
          
          {/* Period Toggle */}
          <div className="flex p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
            <button
              onClick={() => setSelectedPeriod('monthly')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                selectedPeriod === 'monthly'
                  ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-slate-200 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedPeriod('yearly')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                selectedPeriod === 'yearly'
                  ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-slate-200 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Yearly (Save 20%)
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {getProductsForPeriod().map(product => (
            <div 
              key={product.id}
              className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                isSubscribedTo(product.id)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-xl font-bold text-slate-800 dark:text-slate-200">{product.title}</h4>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{product.description}</p>
                </div>
                <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                  {product.priceString}
                  <span className="text-sm text-slate-500 dark:text-slate-400 font-normal">
                    /{product.subscriptionPeriod === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
              </div>
              
              {/* Features */}
              <ul className="space-y-2 mb-6">
                {product.id.includes('pro') ? (
                  <>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Up to 10 statuses</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>50 participants per status</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>5 broadcast links per status</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Priority support</span>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Unlimited statuses</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>200 participants per status</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Unlimited broadcast links</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>24/7 dedicated support</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Advanced security features</span>
                    </li>
                  </>
                )}
              </ul>
              
              <button
                onClick={() => handlePurchase(product.id)}
                disabled={isSubscribedTo(product.id) || loading}
                className={`w-full py-2 px-4 rounded-lg transition-all duration-200 text-sm font-medium ${
                  isSubscribedTo(product.id)
                    ? 'bg-green-500 text-white cursor-default'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                }`}
              >
                {isSubscribedTo(product.id) ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Current Plan
                  </span>
                ) : (
                  <span>Subscribe</span>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Restore Purchases */}
      <div className="mt-8 text-center">
        <button
          onClick={handleRestorePurchases}
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <RefreshCw className="w-4 h-4" />
          Restore Purchases
        </button>
      </div>

      {/* Built with Bolt.new Badge */}
      <div className="mt-8 flex justify-center">
        <BoltBadge />
      </div>
    </div>
  );
};

export default SubscriptionManager;